<template lang="pug">
  .support-metrics(v-loading="loading")
    .support-page-header
      h1 Soporte · Métricas
      .support-page-actions
        el-date-picker(
          v-model="range"
          type="daterange"
          range-separator="—"
          start-placeholder="Desde"
          end-placeholder="Hasta"
          size="small"
          @change="reload"
        )

    el-row(:gutter="16" v-if="data")
      el-col(:span="6")
        el-card
          .support-metric-card
            .support-metric-label Conversaciones totales
            .support-metric-value {{ data.summary.totalConversations || 0 }}
      el-col(:span="6")
        el-card
          .support-metric-card
            .support-metric-label Resueltas por bot
            .support-metric-value {{ resolvedByBot }}
            .support-metric-pct {{ resolvedByBotPct }}%
      el-col(:span="6")
        el-card
          .support-metric-card
            .support-metric-label Escaladas
            .support-metric-value {{ escalated }}
            .support-metric-pct {{ escalatedPct }}%
      el-col(:span="6")
        el-card
          .support-metric-card
            .support-metric-label CSAT positivo
            .support-metric-value {{ csatPositive }}
            .support-metric-pct {{ csatPositivePct }}%

    el-row(:gutter="16" style="margin-top: 16px" v-if="data")
      el-col(:span="12")
        el-card
          h3 Por estado
          el-table(:data="countsByStatus" size="small")
            el-table-column(prop="_id.status" label="Estado" width="180")
            el-table-column(prop="_id.outcome" label="Outcome" width="180")
            el-table-column(prop="count" label="Cantidad" align="right")
            el-table-column(label="Avg resolución" align="right")
              template(slot-scope="s") {{ formatMs(s.row.avgResolutionMs) }}
            el-table-column(label="Avg first human reply" align="right")
              template(slot-scope="s") {{ formatMs(s.row.avgFirstHumanResponseMs) }}

      el-col(:span="12")
        el-card
          h3 Costos LLM
          .support-metric-card
            .support-metric-label Período seleccionado
            .support-metric-value USD {{ (data.summary.totalCostUsd || 0).toFixed(4) }}
            .support-metric-pct {{ ((data.summary.totalCostUsd || 0) / Math.max(1, data.summary.totalConversations) * 1).toFixed(5) }} USD / conversación
</template>

<script>
import supportService from "@/services/support";

/**
 * @component SupportMetrics
 * @description Dashboard de métricas del soporte (Fase 7 — UC-REG-098).
 *              KPIs: % resueltas por bot, % escaladas, CSAT, costos LLM.
 *              Por estado/outcome.
 * @example <SupportMetrics />
 */
export default {
  name: "SupportMetrics",
  data() {
    const to = new Date();
    const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      data: null,
      loading: false,
      range: [from, to],
    };
  },
  computed: {
    countsByStatus() {
      return (this.data && this.data.counts) || [];
    },
    resolvedByBot() {
      const row = this.countsByStatus.find(
        (c) => c._id && c._id.outcome === "solved_by_bot"
      );
      return row ? row.count : 0;
    },
    escalated() {
      return this.countsByStatus
        .filter(
          (c) =>
            c._id && ["waiting_human", "human_handling"].includes(c._id.status)
        )
        .reduce((sum, c) => sum + c.count, 0);
    },
    csatPositive() {
      return (
        (this.data && this.data.summary && this.data.summary.positiveCsat) || 0
      );
    },
    csatTotal() {
      const s = this.data && this.data.summary;
      return s ? (s.positiveCsat || 0) + (s.negativeCsat || 0) : 0;
    },
    total() {
      return (
        (this.data &&
          this.data.summary &&
          this.data.summary.totalConversations) ||
        0
      );
    },
    resolvedByBotPct() {
      return this.total > 0
        ? Math.round((this.resolvedByBot / this.total) * 100)
        : 0;
    },
    escalatedPct() {
      return this.total > 0
        ? Math.round((this.escalated / this.total) * 100)
        : 0;
    },
    csatPositivePct() {
      return this.csatTotal > 0
        ? Math.round((this.csatPositive / this.csatTotal) * 100)
        : 0;
    },
  },
  mounted() {
    this.reload();
  },
  methods: {
    /**
     * @description Refresca métricas con el rango actual.
     */
    async reload() {
      this.loading = true;
      try {
        this.data = await supportService.getMetrics({
          from: this.range[0].toISOString(),
          to: this.range[1].toISOString(),
        });
      } catch (err) {
        this.$message.error("No pude cargar métricas.");
      } finally {
        this.loading = false;
      }
    },
    /**
     * @description Formatea ms a "Xh Ym" o "Ym" o "—".
     * @param {number} ms
     * @returns {string}
     */
    formatMs(ms) {
      if (!ms) return "—";
      const min = Math.round(ms / 60000);
      if (min < 60) return `${min}m`;
      const hr = Math.floor(min / 60);
      return `${hr}h ${min - hr * 60}m`;
    },
  },
};
</script>

<style lang="scss" scoped>
.support-page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  h1 {
    margin: 0;
    font-size: 22px;
    color: #002d5e;
  }
}
.support-metric-card {
  padding: 8px 0;
  .support-metric-label {
    font-size: 12px;
    color: #6b7294;
  }
  .support-metric-value {
    font-size: 28px;
    font-weight: 700;
    color: #002d5e;
  }
  .support-metric-pct {
    font-size: 13px;
    color: #05e5ba;
    font-weight: 600;
  }
}
</style>
